import { useEffect, useMemo, useState } from "react";
import {
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Button,
  Spinner,
  Text,
} from "@fluentui/react-components";
import {
  Dismiss16Regular,
  History16Regular,
  History20Regular,
} from "@fluentui/react-icons";
import { useQuery } from "react-query";
import { getAdoRefreshHistory, IAdoRunRecord } from "../../api/aiMetricsApi";
import { ADO_QUERY_KEYS } from "../../hooks/adoQueryKeys";
import { useHistoryStyles } from "./RefreshControl.styles";
import { formatLocalDateTime } from "utils/generalUtils";
import { QueryCacheConfig } from "common/constants";

const HISTORY_LIMIT = 20;

const formatTrigger = (trigger: IAdoRunRecord["trigger"]): string =>
  trigger === "on_demand" ? "Manual" : "Scheduled";

const formatTriggeredBy = (record: IAdoRunRecord): string => {
  if (record.trigger === "scheduled") return "System";
  return record.triggered_by?.display_name ?? "—";
};

const STATUS_LABELS: Record<IAdoRunRecord["status"], string> = {
  pending: "Pending",
  running: "Running",
  success: "Success",
  failure: "Failed",
  discarded: "Discarded",
};

const formatStatus = (status: IAdoRunRecord["status"]): string =>
  STATUS_LABELS[status] ?? status;

export const RefreshHistoryPopover = (): JSX.Element => {
  const historyStyles = useHistoryStyles();
  const [isOpen, setIsOpen] = useState(false);

  const { data: historyItems, isLoading } = useQuery({
    queryKey: [ADO_QUERY_KEYS.refreshHistory],
    queryFn: () => getAdoRefreshHistory(HISTORY_LIMIT),
    staleTime: QueryCacheConfig.STALE_TIME_SHORT,
  });

  const rows = useMemo(() => historyItems ?? [], [historyItems]);

  useEffect(() => {
    if (!isOpen) return;
    const handleScroll = (event: Event): void => {
      const target = event.target as HTMLElement | null;
      if (target?.closest?.("[data-refresh-history-surface]")) return;
      setIsOpen(false);
    };
    window.addEventListener("scroll", handleScroll, true);
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, [isOpen]);

  return (
    <Popover
      positioning="below-end"
      trapFocus
      open={isOpen}
      onOpenChange={(_, data) => setIsOpen(data.open)}
    >
      <PopoverTrigger disableButtonEnhancement>
        <Button
          appearance="subtle"
          size="small"
          icon={<History16Regular />}
          aria-label="View refresh history"
          className={historyStyles.historyButton}
        />
      </PopoverTrigger>
      <PopoverSurface
        className={historyStyles.popoverSurface}
        role="dialog"
        aria-label="Refresh history"
        data-refresh-history-surface
      >
        <div className={historyStyles.popoverHeader}>
          <span className={historyStyles.popoverHeaderIcon} aria-hidden="true">
            <History20Regular />
          </span>
          <div className={historyStyles.popoverHeaderText}>
            <Text className={historyStyles.popoverTitle}>Refresh History</Text>
            <Text className={historyStyles.popoverSubtitle}>
              Recent Azure DevOps metric refreshes
            </Text>
          </div>
          <Button
            appearance="subtle"
            size="small"
            icon={<Dismiss16Regular />}
            aria-label="Close refresh history"
            className={historyStyles.popoverCloseButton}
            onClick={() => setIsOpen(false)}
          />
        </div>
        {isLoading ? (
          <div className={historyStyles.loadingRow}>
            <Spinner size="tiny" label="Loading history..." />
          </div>
        ) : rows.length === 0 ? (
          <Text className={historyStyles.emptyText}>No records found.</Text>
        ) : (
          <>
            <table className={historyStyles.headerTable}>
              <colgroup>
                <col className={historyStyles.colDate} />
                <col className={historyStyles.colDate} />
                <col className={historyStyles.colTrigger} />
                <col className={historyStyles.colBy} />
                <col className={historyStyles.colStatus} />
              </colgroup>
              <thead>
                <tr>
                  <th scope="col" className={historyStyles.th}>
                    Started At
                  </th>
                  <th scope="col" className={historyStyles.th}>
                    Finished At
                  </th>
                  <th scope="col" className={historyStyles.th}>
                    Trigger
                  </th>
                  <th scope="col" className={historyStyles.th}>
                    By
                  </th>
                  <th scope="col" className={historyStyles.th}>
                    Status
                  </th>
                </tr>
              </thead>
            </table>
            <div
              className={historyStyles.popoverBody}
              data-refresh-history-surface
            >
              <table className={historyStyles.bodyTable}>
                <colgroup>
                  <col className={historyStyles.colDate} />
                  <col className={historyStyles.colDate} />
                  <col className={historyStyles.colTrigger} />
                  <col className={historyStyles.colBy} />
                  <col className={historyStyles.colStatus} />
                </colgroup>
                <tbody>
                  {rows.map((record) => (
                    <tr key={record.id} className={historyStyles.tr}>
                      <td className={historyStyles.td}>
                        {formatLocalDateTime(record.started_at)}
                      </td>
                      <td className={historyStyles.td}>
                        {formatLocalDateTime(record.finished_at)}
                      </td>
                      <td className={historyStyles.td}>
                        {formatTrigger(record.trigger)}
                      </td>
                      <td className={historyStyles.td}>
                        {formatTriggeredBy(record)}
                      </td>
                      <td className={historyStyles.td}>
                        <span
                          className={
                            record.status === "failure" ||
                            record.status === "discarded"
                              ? historyStyles.statusFailure
                              : record.status === "success"
                                ? historyStyles.statusSuccess
                                : historyStyles.statusRunning
                          }
                        >
                          {formatStatus(record.status)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </PopoverSurface>
    </Popover>
  );
};
